import React, { FC, useEffect, useState } from 'react'
import { Button, Checkbox, ConfigProvider, Drawer, Flex, Form, Input, Segmented, Select, Space, Typography } from 'antd'
import { useService } from '@pushly/aqe/lib/hooks/use-service'
import { PageAnalysisRuleState, PageAnalysisScope, UrlRuleMatchType, UrlRulePolicy } from '@pushly/models/lib/enums'
import { MATCH_TYPE_LABELS, POLICY_LABELS, RULE_STATE_DESCRIPTIONS, RULE_STATE_LABELS, SCOPE_LABELS } from './constants'
import { DEFAULT_DRAWER_FORM_ITEM_LAYOUT } from '../../constants'
import { PageAnalysisGateService } from '../../services/page-analysis-gate.service'
import { IPageAnalysisUrlRule } from '../../interfaces/page-analysis-gates'
import { simpleNotification } from '../../_utils/utils'

interface IGateRuleDrawer {
    open: boolean
    domainId?: number
    /** The path the operator was looking at, pre-selected so the common case is one click. */
    scope: PageAnalysisScope
    /** The rule being edited. Absent means adding one. */
    rule?: IPageAnalysisUrlRule
    onClose: () => void
    onSaved: () => void
}

const POLICY_OPTIONS = [UrlRulePolicy.DENY, UrlRulePolicy.ALLOW].map((policy) => ({
    value: policy,
    label: POLICY_LABELS[policy],
}))

const STATE_OPTIONS = [PageAnalysisRuleState.DISABLED, PageAnalysisRuleState.TRACE, PageAnalysisRuleState.ACTIVE].map(
    (state) => ({ value: state, label: RULE_STATE_LABELS[state] }),
)

const MATCH_TYPE_OPTIONS = Object.values(UrlRuleMatchType).map((matchType) => ({
    value: matchType,
    label: MATCH_TYPE_LABELS[matchType],
}))

const SCOPE_OPTIONS = [PageAnalysisScope.DIRECT, PageAnalysisScope.ETL].map((scope) => ({
    value: scope,
    label: SCOPE_LABELS[scope],
}))

export const GateRuleDrawer: FC<IGateRuleDrawer> = ({ open, domainId, scope, rule, onClose, onSaved }) => {
    const gateService = useService<PageAnalysisGateService>(PageAnalysisGateService)

    const [policy, setPolicy] = useState<UrlRulePolicy>(UrlRulePolicy.DENY)
    const [matchType, setMatchType] = useState<UrlRuleMatchType>(UrlRuleMatchType.PATH_PREFIX)
    const [value, setValue] = useState('')
    const [scopes, setScopes] = useState<PageAnalysisScope[]>([scope])
    const [state, setState] = useState<PageAnalysisRuleState>(PageAnalysisRuleState.TRACE)
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        if (!open) {
            return
        }

        if (!rule) {
            setPolicy(UrlRulePolicy.DENY)
            setMatchType(UrlRuleMatchType.PATH_PREFIX)
            setValue('')
            setScopes([scope])
            setState(PageAnalysisRuleState.TRACE)
            return
        }

        setPolicy(rule.policy)
        setMatchType(rule.matchType)
        setValue(rule.value)
        setScopes(rule.scopes)
        setState(rule.state)
    }, [open, scope, rule])

    const submit = async () => {
        if (!domainId) {
            return
        }

        setSaving(true)

        // The edit sends the whole rule, which the route requires. `description`
        // is carried through rather than authored here, so editing a rule does
        // not clear a note someone left on it.
        const res = rule
            ? await gateService.updateUrlRule(domainId, rule.id, {
                  policy,
                  matchType,
                  value: value.trim(),
                  scopes,
                  state,
                  description: rule.description,
              })
            : await gateService.createUrlRule(domainId, {
                  policy,
                  matchType,
                  value: value.trim(),
                  scopes,
                  state,
                  description: null,
              })

        setSaving(false)

        // aqe reports failure through `ok`; `error` is never populated.
        if (!res.ok) {
            simpleNotification('error', 'The rule could not be saved.')
            return
        }

        onSaved()
        onClose()
    }

    return (
        <Drawer
            title={rule ? 'Edit a URL rule' : 'Add a URL rule'}
            placement="right"
            width={600}
            open={open}
            onClose={onClose}
            destroyOnHidden={true}
            footer={
                <div style={{ textAlign: 'right' }}>
                    <Space>
                        <Button onClick={onClose}>Cancel</Button>
                        <Button
                            type="primary"
                            onClick={submit}
                            loading={saving}
                            disabled={!value.trim() || scopes.length === 0}
                        >
                            {rule ? 'Save rule' : 'Add rule'}
                        </Button>
                    </Space>
                </div>
            }
        >
            <ConfigProvider theme={{ components: { Form: { itemMarginBottom: 16 } } }}>
                <Form {...DEFAULT_DRAWER_FORM_ITEM_LAYOUT} labelAlign="left" requiredMark={false}>
                    <Form.Item label="This rule">
                        <Segmented
                            value={policy}
                            onChange={(next) => setPolicy(next as UrlRulePolicy)}
                            options={POLICY_OPTIONS}
                        />
                    </Form.Item>

                    <Form.Item label="Match">
                        <Space.Compact style={{ width: '100%' }}>
                            <Select
                                value={matchType}
                                onChange={(next) => setMatchType(next)}
                                options={MATCH_TYPE_OPTIONS}
                                style={{ width: 200 }}
                            />
                            <Input value={value} onChange={(ev) => setValue(ev.target.value)} placeholder="/tag/" />
                        </Space.Compact>
                    </Form.Item>

                    <Form.Item label="Applies to">
                        <Checkbox.Group
                            value={scopes}
                            onChange={(next) => setScopes(next as PageAnalysisScope[])}
                            options={SCOPE_OPTIONS}
                        />
                    </Form.Item>

                    <Form.Item label="State">
                        <Flex vertical gap={4} align="flex-start">
                            <Segmented
                                value={state}
                                onChange={(next) => setState(next as PageAnalysisRuleState)}
                                options={STATE_OPTIONS}
                            />
                            <Typography.Text className="ai-muted-text">
                                {RULE_STATE_DESCRIPTIONS[state]}
                            </Typography.Text>
                        </Flex>
                    </Form.Item>

                    {policy === UrlRulePolicy.ALLOW && (
                        <Form.Item label=" " colon={false}>
                            <Typography.Text className="ai-muted-text">
                                An allow rule states what a path is for rather than adding one more thing to include.
                                While it is enforced, a page matching no allow rule is declined even though no rule
                                names it.
                            </Typography.Text>
                        </Form.Item>
                    )}
                </Form>
            </ConfigProvider>
        </Drawer>
    )
}
